import React, { useState, useEffect } from 'react';
import {
  isNative,
  getSettings,
  saveSettings,
  requestPermission,
  scheduleDailyReminder,
  cancelDailyReminder,
  ReminderSettings as Settings,
} from '../../services/notifications';

export const ReminderSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings>(getSettings);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const native = isNative();

  // Persist and schedule/cancel whenever settings change
  useEffect(() => {
    saveSettings(settings);
    if (!native) return;

    if (settings.enabled) {
      scheduleDailyReminder(settings.hour, settings.minute);
    } else {
      cancelDailyReminder();
    }
  }, [settings, native]);

  const handleToggle = async () => {
    if (!settings.enabled && native) {
      try {
        const granted = await requestPermission();
        if (!granted) {
          setPermissionDenied(true);
          return;
        }
        setPermissionDenied(false);
      } catch (err) {
        console.error('Notification permission error:', err);
        setPermissionDenied(true);
        return;
      }
    }
    setSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  // Format as HH:MM for the native time input
  const timeValue = `${String(settings.hour).padStart(2, '0')}:${String(settings.minute).padStart(2, '0')}`;

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [h, m] = e.target.value.split(':').map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      setSettings(prev => ({ ...prev, hour: h, minute: m }));
    }
  };

  return (
    <div className="reminder-settings">
      <h3>Daily Reminder</h3>

      {!native && (
        <p className="reminder-web-note">
          Push reminders are available in the mobile app. Download RepsLearn from the Play Store to get daily practice reminders.
        </p>
      )}

      {native && (
        <>
          <div className="reminder-toggle-row">
            <span>Remind me to practice daily</span>
            <button
              className={`toggle-switch ${settings.enabled ? 'active' : ''}`}
              onClick={handleToggle}
              role="switch"
              aria-checked={settings.enabled}
            >
              <span className="toggle-knob" />
            </button>
          </div>

          {permissionDenied && (
            <p className="reminder-error">
              Notification permission denied. Please enable notifications in your device settings.
            </p>
          )}

          {settings.enabled && (
            <div className="reminder-time-picker">
              <label>Reminder time:</label>
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className="time-input"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
